import { IModules, INotifiers } from "lib/millennium.core";
import { Client } from "lib/wts.client";
import { IMillennium } from "lib/millennium.core"
// @ts-ignore — jquery-3.7.1.min.js sem tipos. Apenas exposição global.
import * as _jquery from "jquery"
import * as _ko from "knockout"
import _moment from "moment"

if (!(window as any).millennium) (window as any).millennium = {} as Partial<IMillennium>

millennium.lib = {
  get "wts.ui.controls"() {
    return import("lib/wts.ui.controls")
  },
  get "wts.ui.templating"() {
    return import("lib/wts.ui.templating")
  },
  get "wts.ui.templating.lit"() {
    return import("lib/wts.ui.templating.lit")
  },
  get "wts.ui.overlays"() {
    return import("lib/wts.ui.overlays")
  },
  get "wts.ui.onboarding"() {
    return import("lib/wts.ui.onboarding")
  },
  get "wts.ui.feedback"() {
    return import("lib/wts.ui.feedback")
  },
  get "wts.client"() {
    return import("lib/wts.client")
  },
  // Módulos do common-js compartilhados — expostos para extensões consumirem via webpack
  // externals em vez de bundlar uma cópia local. Sem isso, importar `MethodView` da extensão
  // arrasta KO, jQuery, SlickGrid, moment, etc. (centenas de KB por bundle).
  get "wts.ui.data"() {
    return import("lib/wts.ui.data")
  },
  get "wts.ui.frame"() {
    return import("lib/wts.ui.frame")
  },
  get "wts.ui.dataform"() {
    return import("lib/wts.ui.dataform")
  },
  get "wts.ui.editors.slick"() {
    return import("lib/wts.ui.editors.slick")
  },
  // `wts.ui.editors` importa `./wts.ui.data` por caminho RELATIVO. Sem estar aqui (e no
  // WTS_LIB_NAMES do webpack.config.lib), uma lib externa que o importe bundla uma CÓPIA
  // de wts.ui.data — e os `instanceof Field/LookupField` dos editores passam a testar
  // contra classes diferentes das do core, fazendo todo editor lançar "Field expected".
  get "wts.ui.editors"() {
    return import("lib/wts.ui.editors")
  },
  // Re-expostos para libs externas (omni, extensões) consumirem via webpack externals em
  // vez de bundlar uma cópia local — garante singleton. Permite `import { html } from 'lit-html'`
  // no source, condição para @lit-labs/compiler otimizar templates AOT.
  get "lit-html"() {
    return import("lit-html")
  },
  get "lit-html/directives/repeat.js"() {
    return import("lit-html/directives/repeat.js")
  },
  get "lit-html/directives/unsafe-html.js"() {
    return import("lit-html/directives/unsafe-html.js")
  },
  get "lit-html/directives/class-map.js"() {
    return import("lit-html/directives/class-map.js")
  },
  // Importado pelo output AOT do @lit-labs/compiler (CompiledTemplate prepareTemplate).
  get "lit-html/private-ssr-support.js"() {
    return import("lit-html/private-ssr-support.js")
  },
}

// Vendor libs SÍNCRONAS hospedadas em `millennium.lib`. Não dá pra usar Promise externals
// para essas (igual lit-html) porque o common-js inclui scripts legacy CJS/AMD
// (`knockout.validation.js`, `bootstrap-datepicker.js`) que fazem `require("knockout")`
// síncrono — Promise externals quebrariam esses scripts. Externals em modo `var` apontando
// para `millennium.lib.X` permite imports síncronos sem cópias locais.
// Object.assign evita interceptação do webpack ProvidePlugin (que reescreve atribuições
// literais a `millennium.lib.jQuery = ...` para variável local do bundle).
Object.assign(millennium.lib, {
    jQuery: _jquery,
    $: _jquery,
    ko: _ko,
    moment: _moment,
})

millennium.params = new Map<string, string>(
  location.search.slice(1).split("&").map(e => {
    const parts = e.split("="); return [parts[0]?.toLocaleLowerCase(), parts[1]]
  }))

// Registry de módulos/páginas. INTENCIONALMENTE VAZIO aqui — cada ENTRY popula o que precisa:
//  - `millennium.ts` (core normal) importa `./millennium.modules.pages` → registra as páginas do core;
//  - o bundle dedicado de contingência registra só a sua página (em pos.contingency.boot.ts).
// Assim o shell (`millennium.browser`, que importa este arquivo por loadModule) NÃO arrasta as páginas
// do core — um entry mínimo pode instanciar o <millennium-browser> sem nenhuma página do core no grafo.
export const modules: IModules = {};

const es6module = "es6loader.js";
let es6loader: any;

const moduleNames: { name: string }[] = [];
const moduleInstances = new WeakMap<{ name: string }, any>();

function importModule(fileName: string, moduleName?: string) {

  let nameObj = moduleNames.find(e => e.name === fileName)!;

  if (nameObj) {
    const instance = moduleInstances.get(nameObj);
    if (instance) {
      if (typeof instance === "string" || instance instanceof Error)
        throw instance;

      return instance
    };
  } else {
    nameObj = { name: fileName };
    moduleNames.push(nameObj);
  }

  return new Promise<any>((resolve, reject) => {
    (window as any).exports = {};
    const script = document.createElement("script");
    script.src = fileName;
    script.onload = function (e) {
      if (!moduleName) {
        moduleName = fileName.slice(fileName.lastIndexOf("/") + 1);
        moduleName = moduleName.slice(0, moduleName.lastIndexOf("."));
        if (moduleName.endsWith(".min"))
          moduleName = moduleName.slice(0, moduleName.lastIndexOf("."));
      }

      const instance = (moduleName && (window as any)[moduleName]) || (window as any).exports;
      moduleInstances.set(nameObj, instance);
      resolve(instance);
    }
    script.onerror = e => {
      const errorStr = "Erro carregando módulo " + nameObj.name;
      moduleInstances.set(nameObj, errorStr);
      reject(errorStr);
    }
    document.head.append(script);
  })
}

export async function loadExtension(name: string): Promise<any> {
  if (name.endsWith(".mjs")) {
    if (!es6loader) es6loader = (await importModule(es6module));
    return es6loader.importES6Module(name);
  }
  else {
    return importModule(name);
  }
}

export async function loadModule(
  name: string
): Promise<{ moduleClass: any; htmlTemplate: string; cssTemplate: string }> {

  let module = modules[name];

  //dynamic module
  if (!module) {
    const libPos = name.indexOf(".");
    if (libPos <= 0)
      throw Error("Nome de módulo inválido: \"" + name + "\" (formato esperado \"biblioteca.modulo\")");
    const libName = name.slice(0, libPos);
    name = name.slice(libPos + 1);
    try {
      const moduleLib = await importModule(libName + "/" + libName + ".js", libName);
      module = moduleLib.default[name];
    } catch {
      throw Error("Library " + libName + " not found");
    }

    if (!module)
      throw Error("Module " + name + " not found in library " + libName)
  }

  return Promise.all([
    module.instance(),
    module.css(),
    module.html()
  ]).then(all => ({
    moduleClass: (all[0] && all[0].default) || all[0] || "",
    cssTemplate: (all[1] && all[1].default) || all[1] || "",
    htmlTemplate: (all[2] && all[2].default) || all[2] || ""
  }));
}

export async function loadNotifiers(): Promise<INotifiers> {
  const notifiers: any[] = [];
  const json = await Client.getJSON<{ module: string }[]>("./millennium.notifiers.json");
  for (const notifierDef of json) {
    const notifierModule = await loadExtension(notifierDef.module);
    notifiers.push(notifierModule);
  }
  return notifiers;
}
